import { Input, RadioGroup } from '../ui/index.ts';
import { useT } from '../i18n/index.ts';
import { cx } from '../ui/cx.ts';

/**
 * Shared effective-from picker for the pricing forms (flat token, tiered
 * token, and unit — the three write faces of a price generation).
 *
 * WHY A MODE AND NOT JUST AN INPUT. Both platform write faces validate
 * `effective_from` with `isIso8601` — a FULL timestamp, never a bare date —
 * and the token face refuses any instant at or before the latest rated event
 * (`MODEL_PRICING_RATED_ANCHOR_CONFLICT`). A date-granularity control can
 * therefore never express the most common operator intent, "from now on":
 * on a day that already has billed usage, today's midnight is always at or
 * before the latest rated event, so the only date the form could submit was
 * tomorrow. "Immediately" — the current instant — is always strictly after
 * every already-rated event, which is exactly what the guard admits.
 *
 * `immediate` is the DEFAULT; `scheduled` keeps each form's existing input
 * (date at local midnight for the token form, datetime-local for the unit
 * form) so the "starts tomorrow" workflow is unchanged.
 */

export type EffectiveFromMode = 'immediate' | 'scheduled';

/**
 * The current instant as the ISO-8601 UTC timestamp the write faces accept.
 * Milliseconds are stripped to match `localDateTimeInputValueToIso`, so the
 * two submit paths spell instants the same way.
 */
export function immediateEffectiveFromIso(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
}

interface Props {
  readonly label: string;
  readonly mode: EffectiveFromMode;
  readonly onModeChange: (mode: EffectiveFromMode) => void;
  /** The scheduled input's draft value; kept even while `immediate` is on. */
  readonly scheduledValue: string;
  readonly onScheduledValueChange: (value: string) => void;
  readonly inputType: 'date' | 'datetime-local';
  readonly disabled: boolean;
  /**
   * The scheduled input's id AND data-testid — the pre-existing per-form ids
   * (`model-pricing-effective-from`, `model-unit-pricing-effective-from`)
   * stay stable; the mode radio group gets `<inputId>-mode`.
   */
  readonly inputId: string;
  readonly className?: string;
}

export function EffectiveFromPicker({
  label,
  mode,
  onModeChange,
  scheduledValue,
  onScheduledValueChange,
  inputType,
  disabled,
  inputId,
  className,
}: Props): JSX.Element {
  const t = useT();
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <span className="text-label text-paper-mid">{label}</span>
      <RadioGroup
        value={mode}
        onValueChange={(next) => {
          if (next === 'immediate' || next === 'scheduled') onModeChange(next);
        }}
        orientation="horizontal"
        ariaLabel={label}
        testId={`${inputId}-mode`}
        options={[
          {
            value: 'immediate',
            label: t('model_detail.pricing_effective.immediate'),
            disabled,
          },
          {
            value: 'scheduled',
            label: t(
              inputType === 'date'
                ? 'model_detail.pricing_effective.scheduled_date'
                : 'model_detail.pricing_effective.scheduled_datetime',
            ),
            disabled,
          },
        ]}
      />
      {mode === 'scheduled' ? (
        <Input
          id={inputId}
          type={inputType}
          mono
          value={scheduledValue}
          onChange={(e) => onScheduledValueChange(e.currentTarget.value)}
          data-testid={inputId}
          disabled={disabled}
          aria-label={label}
        />
      ) : null}
    </div>
  );
}
